import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { capitalize } from 'lodash-es';
import { useMemo } from 'react';
import { StringListCellRenderer } from '../../../../../components/AgTable/cell-renderers/StringListCellRenderer';
import { UNKNOWN_GROUP_NAME } from '../../../../../components/AgTable/tableConstants';
import { IKPIRequestDataModel } from '../../../../../data-models/kpi-requests.data-model';
import { kpiRequestsGridSchema } from '../../../../../data-schemas/KPIRequestSchema';
import { AgFormatterService } from '../../../../../util/ag-formatter-service';
import { FormatterService } from '../../../../../util/formatter-service';
import { DateFormattersConfig } from '../../../../../util/formatters/DateFormatters';
import { StringFormatterId } from '../../../../../util/formatters/StringFormatters';
import { useSchemaToGrid } from '../../../../../util/schema-utils';
import { kpiFiscalDateFormatter } from '../../../common/GridFormatters';

export function companyComparator(a?: { name?: string }, b?: { name: string }) {
  return (a?.name ?? '').localeCompare(b?.name ?? '');
}

export function useCommonKpiColDefs() {
  const schemaToGrid = useSchemaToGrid();

  const commonCompanyDefs: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      ...schemaToGrid(kpiRequestsGridSchema(), ['companyId']).at(0),
      field: 'companyId',
      filter: 'agSetColumnFilter',
    }),
    [schemaToGrid]
  ) as ColDef<IKPIRequestDataModel>;

  const nonSelectableCompanyColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      ...commonCompanyDefs,
      enableRowGroup: true,
    }),
    [commonCompanyDefs]
  );

  const respondentColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      field: 'respondent',
      headerName: 'Respondent',
      cellRenderer: StringListCellRenderer,
      cellRendererParams: {
        title: 'Respondents',
      },
      minWidth: 200,
      filter: 'agSetColumnFilter',
    }),
    []
  );

  const secondaryRespondentColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      ...respondentColDef,
      field: 'secondaryRespondents',
      headerName: 'CC Email',
    }),
    [respondentColDef]
  );

  const periodColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      colId: 'period',
      headerName: 'Period',
      valueGetter: (params: ValueGetterParams<IKPIRequestDataModel>) =>
        params.data && kpiFiscalDateFormatter(params),
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const a = nodeA?.data?.period ?? '';
        const b = nodeB?.data?.period ?? '';
        return a.localeCompare(b);
      },
      enableRowGroup: true,
      keyCreator: (params) => {
        if (!params.value) {
          return '';
        }
        return params.value ?? UNKNOWN_GROUP_NAME;
      },
      filter: 'agSetColumnFilter',
    }),
    []
  );

  const templateColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () =>
      ({
        ...schemaToGrid(kpiRequestsGridSchema(), ['templateUuid']).at(0),
        field: 'templateUuid',
        headerName: 'Template',
        filter: true,
      }) as ColDef,
    [schemaToGrid]
  );

  const fyeColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      field: 'fye',
      headerName: 'FYE',
      valueGetter: fyeValueFormatter,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueGetter: fyeValueFormatter,
      },
    }),
    []
  );

  const frequencyColDef: ColDef<IKPIRequestDataModel> = useMemo(
    () => ({
      field: 'frequency',
      headerName: 'Frequency',
      valueFormatter: (params) => {
        return capitalize(params.value ?? '');
      },
      enableRowGroup: true,
      filter: true,
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => capitalize(params.value ?? ''),
      },
    }),
    []
  );

  const granularityColDef: ColDef<IKPIRequestDataModel> = {
    field: 'granularity',
    headerName: 'Granularity',
    valueFormatter: AgFormatterService.get().getFormatterForId(StringFormatterId.capitalizedString),
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: AgFormatterService.get().getFormatterForId(StringFormatterId.capitalizedString),
    },
  };

  return {
    commonCompanyDefs,
    fyeColDef,
    granularityColDef,
    nonSelectableCompanyColDef,
    frequencyColDef,
    respondentColDef,
    periodColDef,
    secondaryRespondentColDef,
    templateColDef,
  };
}

function fyeValueFormatter(params: ValueGetterParams<IKPIRequestDataModel>) {
  return params.data?.fye
    ? FormatterService.get().getFormatterForModel(DateFormattersConfig.monthLong)(params.data?.fye as string)
    : '';
}
